import { createSlice, PayloadAction, } from '@reduxjs/toolkit';
import { changePanel, fetchRoofData } from '../RoofDataSlice/roofDataSlice';

export enum EDIT_POINT_ACTION{
	UNMERGE_POINT= 'unmerge',
	DELETE_POINT = 'delete',
	INSERT_POINT = 'insert',
	// ...
}

type RoofState = {
	data: {
		editableFacets:editableFacet[];
		enableEditFacet: boolean;
		enableDeletePoints: boolean;
		facetHistory: { editableFacets: editableFacet[], mergeDeletedFacetIds: string[], mergeTargetFacetId: string | null }[];
		currentIndex: number;
		isEdited: boolean;
		isSelected: boolean;
		selectedRoofID: string | null;
		showFacetLabels: boolean;
		isInteractingWithCommonPoint: boolean;
		editPointAction?: EDIT_POINT_ACTION;
		invalidFacetIDs: string[];
		pointSelected: boolean;
		enableMergeFacets: boolean;
		mergeSelectedFacetIds: string[];
		/** facets removed by merging, part of the edit-facet undo/redo history */
		mergeDeletedFacetIds: string[];
		/** facet holding the result of the last merge; stays selected but is shown as edited */
		justMergedFacetId: string | null;
	}

}

const initialState: RoofState = {data: {
	editableFacets: [], 
	enableEditFacet: false,
	enableDeletePoints: false,
	isEdited: false,
	isSelected: false,
	facetHistory: [],
	currentIndex: -1,
	selectedRoofID: null,
	showFacetLabels: false,
	isInteractingWithCommonPoint:false,
	editPointAction: undefined,
	invalidFacetIDs: [],
	pointSelected: false,
	enableMergeFacets: false,
	mergeSelectedFacetIds: [],
	mergeDeletedFacetIds: [],
	justMergedFacetId: null,
}};

export const roofEditSlice = createSlice({
	name: 'roofData',
	initialState,
	reducers: {
		setEditableFacets: (state, action: PayloadAction<{ editableFacets: editableFacet[] }>) => {
			const newFacets = action.payload.editableFacets;
			if (state.data.currentIndex === -1 || state.data.facetHistory[state.data.currentIndex].editableFacets !== newFacets) {
				state.data.facetHistory = [
					...state.data.facetHistory.slice(0, state.data.currentIndex + 1),
					{
						editableFacets: newFacets,
						mergeDeletedFacetIds: state.data.mergeDeletedFacetIds,
						mergeTargetFacetId: state.data.justMergedFacetId,
					},
				];
				state.data.currentIndex = state.data.facetHistory.length - 1;
			}

			state.data.editableFacets = newFacets;
		},
		setFacetIntersections: (state, action: PayloadAction<string[]>) => {
			state.data.invalidFacetIDs = action.payload;
		},
		setDeletePoints: (state, action: PayloadAction<{ deletePoints: boolean }>) => {
			state.data.enableDeletePoints = action.payload.deletePoints;
		},
		undoEditableFacets: (state) => {
			if (state.data.currentIndex > 0) {
				state.data.currentIndex -= 1;
				const historyEntry = state.data.facetHistory[state.data.currentIndex];
				state.data.editableFacets = historyEntry.editableFacets;
				state.data.mergeDeletedFacetIds = historyEntry.mergeDeletedFacetIds ?? [];
				// merge results are never left selected, so a restored state starts clean
				state.data.justMergedFacetId = historyEntry.mergeTargetFacetId ?? null;
				state.data.mergeSelectedFacetIds = [];
			}
		},
		redoEditableFacets: (state) => {
			if (state.data.currentIndex < state.data.facetHistory.length - 1) {
				state.data.currentIndex += 1;
				const historyEntry = state.data.facetHistory[state.data.currentIndex];
				state.data.editableFacets = historyEntry.editableFacets;
				state.data.mergeDeletedFacetIds = historyEntry.mergeDeletedFacetIds ?? [];
				// merge results are never left selected, so a restored state starts clean
				state.data.justMergedFacetId = historyEntry.mergeTargetFacetId ?? null;
				state.data.mergeSelectedFacetIds = [];
			}
		},
		setEnableFacetEdit: (state, action:PayloadAction<{isEnable: boolean}>) => {
			state.data.enableEditFacet = action.payload.isEnable;
			if(!action.payload.isEnable) {
				state.data.editableFacets = [];
				state.data.facetHistory = [];
				state.data.currentIndex = -1;
			}
			state.data.editPointAction = undefined;
			state.data.invalidFacetIDs = [];
			state.data.enableMergeFacets = false;
			state.data.mergeSelectedFacetIds = [];
			state.data.mergeDeletedFacetIds = [];
			state.data.justMergedFacetId = null;
		},
		deletePointsBatch: (state, action: PayloadAction<{ pointIndex: number, segmentId: string }[]>) => {
			const editableFacets = state.data.editableFacets;
			const deletionMap = new Map<string, Set<number>>();
		
			// Group point indices by segmentId
			action.payload.forEach(({ segmentId, pointIndex }) => {
				if (!deletionMap.has(segmentId)) {
					deletionMap.set(segmentId, new Set());
				}
				deletionMap.get(segmentId)!.add(pointIndex);
			});
		
			const updatedFacets = editableFacets.map(facet => {
				const deleteIndices = deletionMap.get(facet.segmentId);
				if (!deleteIndices) return facet;
		
				let newCoordinates = facet.coordinates.filter((_, i) => !deleteIndices.has(i * 2));
				const uniqueCoordinates:any = [];

				for (const [x, y] of newCoordinates) {
					const isDuplicate = uniqueCoordinates.some(([ux, uy]:any) => {
						return (
							Math.abs(x - ux) <= 3 &&
							Math.abs(y - uy) <= 3
						);
					});
					if (!isDuplicate) {
						uniqueCoordinates.push([x, y]);
					}
				}
				if(uniqueCoordinates.length < 3) {
					newCoordinates = [];
				}

				// If not enough points left, leave unchanged
				return {
					...facet,
					coordinates: newCoordinates,
					isEdited: true,
				};
			});
		
			state.data.facetHistory = [
				...state.data.facetHistory.slice(0, state.data.currentIndex + 1),
				{
					editableFacets: updatedFacets,
					mergeDeletedFacetIds: state.data.mergeDeletedFacetIds,
					mergeTargetFacetId: state.data.justMergedFacetId,
				},
			];
			state.data.currentIndex = state.data.facetHistory.length - 1;
			state.data.editableFacets = updatedFacets;
		},

		toggleFacetLabels: (state, action: PayloadAction<{val: boolean}>)=>{
			state.data.showFacetLabels = action.payload.val;
		},

		setEditFacetAction: (state, action: PayloadAction<{actionType: RoofState['data']['editPointAction']}>)=>{
			state.data.editPointAction = action.payload.actionType;
			if (action.payload.actionType) {
				state.data.enableMergeFacets = false;
				state.data.mergeSelectedFacetIds = [];
			}
		},
		setPointSelected: (state, action: PayloadAction<{isSelected: boolean}>) => {
			state.data.pointSelected = action.payload.isSelected;
		},
		setEnableMergeFacets: (state, action: PayloadAction<{isEnable: boolean}>) => {
			state.data.enableMergeFacets = action.payload.isEnable;
			state.data.mergeSelectedFacetIds = [];
			state.data.justMergedFacetId = null;
			// merging and point actions can't be active together
			if (action.payload.isEnable) {
				state.data.editPointAction = undefined;
			}
		},
		toggleMergeFacetSelection: (state, action: PayloadAction<{segmentId: string}>) => {
			const { segmentId } = action.payload;
			const selected = state.data.mergeSelectedFacetIds;
			state.data.mergeSelectedFacetIds = selected.includes(segmentId)
				? selected.filter(id => id !== segmentId)
				: [...selected, segmentId];
			state.data.justMergedFacetId = null;
		},
		clearMergeFacetSelection: (state) => {
			state.data.mergeSelectedFacetIds = [];
			state.data.justMergedFacetId = null;
		},
		applyFacetMerge: (state, action: PayloadAction<{ mergedFacet: editableFacet, removedFacetIds: string[] }>) => {
			const { mergedFacet, removedFacetIds } = action.payload;

			const updatedFacets = state.data.editableFacets
				.filter(facet => !removedFacetIds.includes(facet.segmentId))
				.map(facet => facet.segmentId === mergedFacet.segmentId ? mergedFacet : facet);
			const mergeDeletedFacetIds = [...state.data.mergeDeletedFacetIds, ...removedFacetIds];

			state.data.facetHistory = [
				...state.data.facetHistory.slice(0, state.data.currentIndex + 1),
				{ editableFacets: updatedFacets, mergeDeletedFacetIds, mergeTargetFacetId: mergedFacet.segmentId },
			];
			state.data.currentIndex = state.data.facetHistory.length - 1;
			state.data.editableFacets = updatedFacets;
			state.data.mergeDeletedFacetIds = mergeDeletedFacetIds;
			// the merge result is a plain single facet again: nothing stays selected, so
			// clicking it starts a fresh merge selection
			state.data.mergeSelectedFacetIds = [];
			state.data.justMergedFacetId = mergedFacet.segmentId;
		}
	},extraReducers: (builder,) => {
		builder.addCase(fetchRoofData.fulfilled, (state) => {
			state.data.enableEditFacet= false;
			state.data.invalidFacetIDs = [];
			state.data.editableFacets = [];
			state.data.facetHistory = [];
			state.data.currentIndex = -1;
			state.data.editPointAction = undefined;
			state.data.enableMergeFacets = false;
			state.data.mergeSelectedFacetIds = [];
			state.data.mergeDeletedFacetIds = [];
			state.data.justMergedFacetId = null;

		}).addCase(changePanel.fulfilled, (state, action) => {
			if(action.meta.arg.switchingSource){
				state.data.enableEditFacet= false;
				state.data.invalidFacetIDs = [];
				state.data.editableFacets = [];
				state.data.facetHistory = [];
				state.data.currentIndex = -1;
				state.data.editPointAction = undefined;
				state.data.enableMergeFacets = false;
				state.data.mergeSelectedFacetIds = [];
				state.data.mergeDeletedFacetIds = [];
				state.data.justMergedFacetId = null;
			}

		});
	}
});

export const { 
	setEnableFacetEdit, 
	setEditableFacets, undoEditableFacets, redoEditableFacets,
	setDeletePoints, deletePointsBatch, toggleFacetLabels,
	setEditFacetAction,setFacetIntersections,
	setPointSelected,
	setEnableMergeFacets, toggleMergeFacetSelection, clearMergeFacetSelection, applyFacetMerge
} = roofEditSlice.actions;
export default roofEditSlice.reducer;

